import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../services/api/axios';
import { Workflow, ChevronsLeft, ChevronsRight, Loader2, AlertTriangle, Copy, Grid3X3, Users, Calendar, MoreVertical, Image, Video, FileText, ArrowRight, MessageSquare, Reply, ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import CustomCollapseCard from '../../../components/CustomCollapseCard';
import ConfirmationModal from '../../../components/ConfirmationModal';
import { EditButton, ViewButton } from '../../../components/Buttons';

const PreviousTemplates = ({ project, onViewWorkflow, refreshTrigger }) => {
    const [templates, setTemplates] = useState([]);
    const [replies, setReplies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [groupedWorkflows, setGroupedWorkflows] = useState({});
    const [activeTab, setActiveTab] = useState('saved');
    const [openWorkflow, setOpenWorkflow] = useState(null);
    const [publishConfirmation, setPublishConfirmation] = useState({ show: false, templateId: null });
    const [isPublishing, setIsPublishing] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isLoadingEdit, setIsLoadingEdit] = useState(false);
    const [expandedWorkflows, setExpandedWorkflows] = useState(new Set()); // Track expanded workflows
    const { leadType } = useParams();

    const templateTypeMap = {
        'birthday_template': 'BIRTHDAY_TEMPLATE',
        'birthday_offer_template': 'BIRTHDAY_OFFER_TEMPLATE',
        'festival_template': 'FESTIVAL_TEMPLATE',
        'festival_offer_template': 'FESTIVAL_OFFER_TEMPLATE',
        'conditional': 'CAMPAIGN_CONDITIONAL_TEMPLATE',
        'post_visit': 'POST_VISIT_TEMPLATE',
        'buildvizer': 'BUILDVIZER_TEMPLATE',
        'standard': 'STANDARD_TEMPLATE',
        'reply': 'REPLY_NODE_TEMPLATE'
    };

    const getExpectedTemplateType = () => {
        if (!leadType) return null;
        return templateTypeMap[leadType] || null;
    };

    const getPersonaInfo = (personaId) => {
        const personaMap = {
            1: { name: 'Cold Lead', color: 'bg-blue-100 text-blue-800' },
            2: { name: 'Luke Warm Lead', color: 'bg-purple-100 text-purple-800' },
            3: { name: 'Warm Lead', color: 'bg-orange-100 text-orange-800' },
            4: { name: 'Hot Lead', color: 'bg-red-100 text-red-800' },
            5: { name: 'Customer', color: 'bg-green-100 text-green-800' },
            6: { name: 'Agent', color: 'bg-gray-100 text-gray-800' }
        };
        return personaMap[personaId] || { name: 'Unknown', color: 'bg-gray-100 text-gray-800' };
    };

    const getStatusInfo = (template) => {
        const status = template.whatsappTemplateStatus || 'DRAFT';
        switch (status) {
            case 'PUBLISHED':
                return { status: 'PUBLISHED', color: 'bg-green-100 text-green-800' };
            case 'NOT_PUBLISHED':
                return { status: 'NOT_PUBLISHED', color: 'bg-yellow-100 text-yellow-800' };
            case 'REJECTED':
                return { status: 'REJECTED', color: 'bg-red-100 text-red-800' };
            case 'PENDING':
                return { status: 'PENDING', color: 'bg-orange-100 text-orange-800' };
            case 'DRAFT':
            default:
                return { status: 'DRAFT', color: 'bg-gray-100 text-gray-800' };
        }
    };

    const getComponentCount = (template) => {
        let count = 0;
        if (template.components?.find(c => c.type === 'HEADER')) count++;
        if (template.components?.find(c => c.type === 'BODY')) count++;
        if (template.components?.find(c => c.type === 'BUTTONS')) count++;
        return count;
    };

    const getMediaInfo = (template) => {
        // Check for media in template object (API response format)
        if (template.media?.content && template.media?.content_type) {
            // Ensure the base64 content is properly formatted
            let base64Content = template.media.content;
            
            // If the content doesn't start with data: URL format, add it
            if (!base64Content.startsWith('data:')) {
                base64Content = `data:${template.media.content_type};base64,${base64Content}`;
            }
            
            return {
                hasMedia: true,
                mediaType: template.media.content_type,
                mediaContent: base64Content,
                isImage: template.media.content_type.startsWith('image/'),
                isVideo: template.media.content_type.startsWith('video/'),
                isDocument: template.media.content_type.startsWith('application/')
            };
        }

        // Check for media in header component (local storage format)
        const headerComponent = template.components?.find(c => c.type === 'HEADER');
        if (headerComponent?.format && headerComponent.format !== 'TEXT') {
            return {
                hasMedia: true,
                mediaType: headerComponent.format.toLowerCase(),
                mediaContent: null, // Would need to be fetched separately
                isImage: headerComponent.format === 'IMAGE',
                isVideo: headerComponent.format === 'VIDEO',
                isDocument: headerComponent.format === 'DOCUMENT'
            };
        }

        return { hasMedia: false };
    };

    const renderTemplateCard = (template, isMainTemplate = false) => {
        const mediaInfo = getMediaInfo(template);
        const statusInfo = getStatusInfo(template);
        
        // Debug logging for media
        if (template.media) {
            console.log(`Template ${template.friendlyName} media info:`, {
                hasContent: !!template.media.content,
                contentType: template.media.content_type,
                contentLength: template.media.content?.length || 0,
                mediaInfo: mediaInfo
            });
        }

        return (
            <div className={`bg-white border-2 rounded-lg p-3 min-w-[200px] max-w-[250px] ${isMainTemplate ? 'border-flamingo-300 bg-flamingo-50' : 'border-gray-200'
                }`}>
                {/* Template Header */}
                <div className="flex items-center gap-2 mb-2">
                    {isMainTemplate ? (
                        <MessageSquare size={16} className="text-flamingo-600 flex-shrink-0" />
                    ) : (
                        <Reply size={16} className="text-blue-600 flex-shrink-0" />
                    )}
                    <h5 className="font-medium text-sm text-gray-900 truncate">
                        {template.friendlyName || 'Unnamed Template'}
                    </h5>
                </div>

                {/* Status Badge */}
                <div className="mb-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                        {statusInfo.status}
                    </span>
                </div>

                {/* Media Preview */}
                {mediaInfo.hasMedia && (
                    <div className="mb-2">
                        <div className="flex items-center gap-2 bg-gray-100 rounded p-2">
                            {mediaInfo.mediaContent ? (
                                <>
                                    {mediaInfo.isImage && (
                                        <>
                                            <img
                                                src={mediaInfo.mediaContent}
                                                alt="Template media"
                                                className="w-8 h-8 object-cover rounded border"
                                                onError={(e) => {
                                                    console.error('Image failed to load:', e.target.src);
                                                    e.target.style.display = 'none';
                                                    if (e.target.nextSibling) {
                                                        e.target.nextSibling.style.display = 'block';
                                                    }
                                                }}
                                                onLoad={(e) => {
                                                    console.log('Image loaded successfully:', e.target.src.substring(0, 50) + '...');
                                                }}
                                            />
                                            <div className="hidden">
                                                <Image size={16} className="text-gray-500" />
                                            </div>
                                        </>
                                    )}
                                    {mediaInfo.isVideo && (
                                        <>
                                            <video
                                                src={mediaInfo.mediaContent}
                                                className="w-8 h-8 object-cover rounded border"
                                                muted
                                                onError={(e) => {
                                                    console.error('Video failed to load:', e.target.src);
                                                    e.target.style.display = 'none';
                                                    if (e.target.nextSibling) {
                                                        e.target.nextSibling.style.display = 'block';
                                                    }
                                                }}
                                            />
                                            <div className="hidden">
                                                <Video size={16} className="text-gray-500" />
                                            </div>
                                        </>
                                    )}
                                </>
                            ) : null}
                         
                        </div>
                    </div>
                )}
                           
                

                {/* Template Content Preview */}
                <div className="text-xs text-gray-600">
                    {/* Header Text */}
                    {template.components?.find(c => c.type === 'HEADER')?.text && (
                        <div className="mb-1">
                            <span className="font-medium">Header:</span> {template.components.find(c => c.type === 'HEADER').text.substring(0, 30)}...
                        </div>
                    )}

                    {/* Body Text */}
                    {template.components?.find(c => c.type === 'BODY')?.text && (
                        <div className="mb-1">
                            <span className="font-medium">Body:</span> {template.components.find(c => c.type === 'BODY').text.substring(0, 40)}...
                        </div>
                    )}

                    {/* Buttons Count */}
                    {template.components?.find(c => c.type === 'BUTTONS')?.buttons?.length > 0 && (
                        <div className="text-xs text-blue-600 font-medium">
                            {template.components.find(c => c.type === 'BUTTONS').buttons.length} button(s)
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Returns an object with count and the persona details (name, color) for each personaId
    const getPersonaDetails = (template) => {
        if (!template.persona || !Array.isArray(template.persona)) {
            return { count: 0, personas: [] };
        }

        const personaMap = {
            1: { name: 'Cold Lead', color: 'bg-blue-100 text-blue-800', dotColor: 'bg-blue-500' },
            2: { name: 'Luke Warm Lead', color: 'bg-purple-100 text-purple-800', dotColor: 'bg-purple-500' },
            3: { name: 'Warm Lead', color: 'bg-orange-100 text-orange-800', dotColor: 'bg-orange-500' },
            4: { name: 'Hot Lead', color: 'bg-red-100 text-red-800', dotColor: 'bg-red-500' },
            5: { name: 'Customer', color: 'bg-green-100 text-green-800', dotColor: 'bg-green-500' },
            6: { name: 'Agent', color: 'bg-gray-100 text-gray-800', dotColor: 'bg-gray-500' }
        };
        const personas = template.persona.map(pid => personaMap[parseInt(pid)] || { name: 'Unknown', color: 'bg-gray-100 text-gray-800', dotColor: 'bg-gray-500' });
        return { count: template.persona.length, personas };
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Not set';
        // Handles format like "2025-08-02T07:14:26"
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Not set';
        // Format as "Aug 2, 2025, 7:14 AM"
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        // You could add a toast notification here
    };

    const toggleWorkflowExpansion = (workflowId) => {
        setExpandedWorkflows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(workflowId)) {
                newSet.delete(workflowId);
            } else {
                newSet.add(workflowId);
            }
            return newSet;
        });
    };

    useEffect(() => {
        const fetchTemplates = async () => {
            if (!project?.customerId || !project?.projectId) return;

            try {
                setLoading(true);
                const response = await axiosInstance.get('/customer/whatsapp/drip-campaign', {
                    params: { customerId: project.customerId, projectId: project.projectId }
                });

                setTemplates(response.templates || []);
                setReplies(response.replies || []);

                const grouped = {};
                const destinationIds = new Set(response.replies.map(r => r.destinationTemplate.customerWhatsappTemplateId));
                const startingTemplates = response.templates.filter(t => !destinationIds.has(t.customerWhatsappTemplateId));

                startingTemplates.forEach(start => {
                    const workflowId = `Workflow-${start.customerWhatsappTemplateId}`;
                    const wfTemplates = new Set([start]);
                    const wfConnections = [];
                    const findConnected = (templateId) => {
                        response.replies.forEach(reply => {
                            if (reply.sourceTemplate.customerWhatsappTemplateId === templateId) {
                                const dest = response.templates.find(t => t.customerWhatsappTemplateId === reply.destinationTemplate.customerWhatsappTemplateId);
                                if (dest && !wfTemplates.has(dest)) {
                                    dest.isPublished = start.isPublished;
                                    wfTemplates.add(dest);
                                    wfConnections.push({
                                        source: templateId,
                                        target: dest.customerWhatsappTemplateId,
                                        replyMessage: reply.replyMessage,
                                    });
                                    findConnected(dest.customerWhatsappTemplateId);
                                }
                            }
                        });
                    };
                    findConnected(start.customerWhatsappTemplateId);
                    grouped[workflowId] = {
                        id: workflowId,
                        templates: Array.from(wfTemplates),
                        connections: wfConnections,
                        type: 'with_replies'
                    };
                });

                setGroupedWorkflows(grouped);
            } catch (err) {
                setError('Failed to fetch templates');
            } finally {
                setLoading(false);
            }
        };

        fetchTemplates();
    }, [project, refreshTrigger]);

    const handleViewWorkflow = async (workflowId, isEditing = false) => {
        if (onViewWorkflow) {
            setIsLoadingEdit(true);
            try {
                const workflow = groupedWorkflows[workflowId];
                if (workflow) {
                    const transformedWorkflow = {
                        ...workflow,
                        replies: workflow.connections.map(conn => ({
                            sourceTemplate: { customerWhatsappTemplateId: conn.source },
                            destinationTemplate: { customerWhatsappTemplateId: conn.target },
                            replyMessage: conn.replyMessage
                        }))
                    };
                    await onViewWorkflow(transformedWorkflow, isEditing);
                }
            } catch (error) {
                console.error('Error loading workflow:', error);
                setError('Failed to load workflow');
            } finally {
                setIsLoadingEdit(false);
            }
        }
    };

    const handlePublishClick = (templateId) => {
        setPublishConfirmation({ show: true, templateId });
    };

    const handlePublishCancel = () => {
        setPublishConfirmation({ show: false, templateId: null });
    };

    const handlePublishTemplate = async (templateId) => {
        try {
            // Find the template to publish
            const templateToPublish = templates.find(t => t.customerWhatsappTemplateId === templateId);
            if (!templateToPublish) {
                throw new Error('Template not found');
            }

            // Create form data
            const formData = new FormData();
            formData.append('customer_id', project.customerId);
            formData.append('project_id', project.projectId);
            
            // Convert persona to array of integers and append each separately
            const personaIds = Array.isArray(templateToPublish.persona) 
                ? templateToPublish.persona.map(id => parseInt(id, 10))
                : [1, 2, 3, 4, 5]; // Default values if not provided
            
            personaIds.forEach(id => {
                formData.append('persona', id);
            });
            
            // Add lead_status if it exists
            if (templateToPublish.lead_status) {
                formData.append('lead_status', templateToPublish.lead_status);
            }
            
            formData.append('friendly_name', templateToPublish.friendlyName);
            formData.append('conditions', templateToPublish.conditions || '');
            
            // Create components array
            const componentsArray = [];

            // Add header component if exists
            const headerComponent = templateToPublish.components?.find(c => c.type === 'HEADER');
            if (headerComponent) {
                componentsArray.push(JSON.stringify({
                    type: 'HEADER',
                    format: headerComponent.format || 'TEXT',
                    text: headerComponent.text,
                    example: {
                        header_text: [headerComponent.text]
                    }
                }));
            }

            // Add body component
            const bodyComponent = templateToPublish.components?.find(c => c.type === 'BODY');
            if (bodyComponent) {
                componentsArray.push(JSON.stringify({
                    type: 'BODY',
                    text: bodyComponent.text
                }));
            }

            // Add buttons component if exists
            const buttonsComponent = templateToPublish.components?.find(c => c.type === 'BUTTONS');
            if (buttonsComponent?.buttons) {
                const buttonComponents = buttonsComponent.buttons.map(button => ({
                    type: button.type?.toUpperCase() || 'QUICK_REPLY',
                    text: button.text
                }));
                componentsArray.push(JSON.stringify({
                    type: 'BUTTONS',
                    buttons: buttonComponents
                }));
            }

            // Append each component as a separate string
            componentsArray.forEach(component => {
                formData.append('components', component);
            });
            
            formData.append('template_type', templateToPublish.templateType);
            
            // Handle media properly
            if (templateToPublish.media) {
                // If media is a base64 string, convert it to a File object
                if (typeof templateToPublish.media === 'string' && templateToPublish.media.startsWith('data:')) {
                    const base64Data = templateToPublish.media.split(',')[1];
                    const mimeType = templateToPublish.media.split(',')[0].split(':')[1].split(';')[0];
                    const byteCharacters = atob(base64Data);
                    const byteArrays = [];
                    
                    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
                        const slice = byteCharacters.slice(offset, offset + 512);
                        const byteNumbers = new Array(slice.length);
                        
                        for (let i = 0; i < slice.length; i++) {
                            byteNumbers[i] = slice.charCodeAt(i);
                        }
                        
                        const byteArray = new Uint8Array(byteNumbers);
                        byteArrays.push(byteArray);
                    }
                    
                    const blob = new Blob(byteArrays, { type: mimeType });
                    const file = new File([blob], 'media', { type: mimeType });
                    formData.append('media', file);
                } else if (templateToPublish.media instanceof Blob) {
                    const file = new File([templateToPublish.media], 'media', { type: templateToPublish.media.type });
                    formData.append('media', file);
                } else if (templateToPublish.media instanceof File) {
                    formData.append('media', templateToPublish.media);
                }
            }

            // Make the API call
            await axiosInstance.put('/customer/whatsapp/template', formData, {
                params: {
                    customerWhatsappTemplateId: templateId,
                    isPublished: true
                },
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            // Refresh templates after publishing
            const response = await axiosInstance.get('/customer/whatsapp/drip-campaign', {
                params: {
                    customerId: project.customerId,
                    projectId: project.projectId
                }
            });
            setTemplates(response.templates || []);
            setReplies(response.replies || []);
        } catch (err) {
            console.error('Error publishing template:', err);
            setError('Failed to publish template');
        }
    };

    const handlePublishConfirm = async () => {
        if (!publishConfirmation.templateId) return;

        setIsPublishing(true);
        try {
            await handlePublishTemplate(publishConfirmation.templateId);

            // Show success toast
            toast.success('Workflow published successfully!', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });

            // Switch to published tab
            setActiveTab('published');

            // Refresh the workflows by refetching templates
            const response = await axiosInstance.get('/customer/whatsapp/drip-campaign', {
                params: { customerId: project.customerId, projectId: project.projectId }
            });

            setTemplates(response.templates || []);
            setReplies(response.replies || []);

            // Rebuild grouped workflows
            const grouped = {};
            const destinationIds = new Set(response.replies.map(r => r.destinationTemplate.customerWhatsappTemplateId));
            const startingTemplates = response.templates.filter(t => !destinationIds.has(t.customerWhatsappTemplateId));

            startingTemplates.forEach(start => {
                const workflowId = `Workflow-${start.customerWhatsappTemplateId}`;
                const wfTemplates = new Set([start]);
                const wfConnections = [];
                const findConnected = (templateId) => {
                    response.replies.forEach(reply => {
                        if (reply.sourceTemplate.customerWhatsappTemplateId === templateId) {
                            const dest = response.templates.find(t => t.customerWhatsappTemplateId === reply.destinationTemplate.customerWhatsappTemplateId);
                            if (dest && !wfTemplates.has(dest)) {
                                dest.isPublished = start.isPublished;
                                wfTemplates.add(dest);
                                wfConnections.push({
                                    source: templateId,
                                    target: dest.customerWhatsappTemplateId,
                                    replyMessage: reply.replyMessage,
                                });
                                findConnected(dest.customerWhatsappTemplateId);
                            }
                        }
                    });
                };
                findConnected(start.customerWhatsappTemplateId);
                grouped[workflowId] = {
                    id: workflowId,
                    templates: Array.from(wfTemplates),
                    connections: wfConnections,
                    type: 'with_replies'
                };
            });

            setGroupedWorkflows(grouped);

            // Call refreshTrigger if provided
            if (refreshTrigger && typeof refreshTrigger === 'function') {
                try {
                    refreshTrigger();
                } catch (refreshError) {
                    console.warn('Error calling refreshTrigger:', refreshError);
                }
            }

            setPublishConfirmation({ show: false, templateId: null });
        } catch (error) {
            console.error('Failed to publish template:', error);
            toast.error('Failed to publish template. Please try again.', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        } finally {
            setIsPublishing(false);
        }
    };

    if (loading) {
        return (
            <div className={`bg-white h-full overflow-y-auto border-l rounded-lg shadow-sm border-gray-200 transition-all duration-300 ${isCollapsed ? 'w-16' : 'w-1/3'}`}>
                <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                    {!isCollapsed && (
                        <div>
                            <h2 className="text-lg font-semibold text-gray-800">Previous Workflows</h2>
                            <p className="text-sm text-gray-500">View saved templates</p>
                        </div>
                    )}
                    <button onClick={() => setIsCollapsed(!isCollapsed)} className="p-2 rounded-md hover:bg-gray-200 transition-colors" title={isCollapsed ? "Expand" : "Collapse"}>
                        {isCollapsed ? <ChevronsRight className="w-5 h-5" /> : <ChevronsLeft className="w-5 h-5" />}
                    </button>
                </div>

                {!isCollapsed && (
                    <div className="flex flex-col items-center justify-center h-64">
                        <div className="flex flex-col items-center space-y-4">
                            <div className="w-12 h-12 border-4 border-flamingo-200 border-t-flamingo-500 rounded-full animate-spin"></div>
                            <div className="text-center">
                                <p className="text-lg font-medium text-gray-700">Loading Workflows</p>
                                <p className="text-sm text-gray-500">Fetching your saved templates...</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    if (error) {
        return (
            <div className={`bg-white h-full overflow-y-auto border-l rounded-lg shadow-sm border-gray-200 transition-all duration-300 ${isCollapsed ? 'w-16' : 'w-1/3'}`}>
                <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                    {!isCollapsed && (
                        <div>
                            <h2 className="text-lg font-semibold text-gray-800">Previous Workflows</h2>
                            <p className="text-sm text-gray-500">View saved templates</p>
                        </div>
                    )}
                    <button onClick={() => setIsCollapsed(!isCollapsed)} className="p-2 rounded-md hover:bg-gray-200 transition-colors" title={isCollapsed ? "Expand" : "Collapse"}>
                        {isCollapsed ? <ChevronsRight className="w-5 h-5" /> : <ChevronsLeft className="w-5 h-5" />}
                    </button>
                </div>
                {!isCollapsed && (
                    <div className="flex flex-col items-center justify-center h-64">
                        <div className="flex flex-col items-center space-y-4">
                            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                                <AlertTriangle className="w-6 h-6 text-red-500" />
                            </div>
                            <div className="text-center">
                                <p className="text-lg font-medium text-gray-700">Error Loading Workflows</p>
                                <p className="text-sm text-gray-500">{error}</p>
                                <button onClick={() => window.location.reload()} className="mt-3 px-4 py-2 bg-flamingo-500 text-white rounded-md hover:bg-flamingo-400 transition-colors text-sm">
                                    Try Again
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className={`bg-white h-full overflow-y-auto border-l rounded-lg shadow-sm border-gray-200 transition-all duration-300 ${isCollapsed ? 'w-16' : 'w-1/3'}`}>
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                {!isCollapsed && (
                    <div>
                        <h2 className="text-lg font-semibold text-gray-800">Previous Workflows</h2>
                        <p className="text-sm text-gray-500">View saved templates</p>
                    </div>
                )}
                <button onClick={() => setIsCollapsed(!isCollapsed)} className="p-2 rounded-md hover:bg-gray-200 transition-colors" title={isCollapsed ? "Expand" : "Collapse"}>
                    {isCollapsed ? <ChevronsRight className="w-5 h-5" /> : <ChevronsLeft className="w-5 h-5" />}
                </button>
            </div>
            {!isCollapsed && (
                <>
                    <div className="p-4">
                        <div className="relative mt-4">
                            <div className="flex bg-gray-100 p-1 rounded-lg">
                                <button onClick={() => setActiveTab('saved')} className={`relative flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${activeTab === 'saved' ? 'text-flamingo-500' : 'text-gray-600'}`}>
                                    {activeTab === 'saved' && (<div className="absolute inset-0 bg-white shadow-sm rounded-md transition-all duration-200" />)}
                                    <span className="relative z-10">Saved</span>
                                </button>
                                <button onClick={() => setActiveTab('published')} className={`relative flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${activeTab === 'published' ? 'text-flamingo-500' : 'text-gray-600'}`}>
                                    {activeTab === 'published' && (<div className="absolute inset-0 bg-white shadow-sm rounded-md transition-all duration-200" />)}
                                    <span className="relative z-10">Published</span>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="p-4 space-y-6">
                        {Object.values(groupedWorkflows)
                            .filter(workflow => {
                                if (workflow.type !== 'with_replies') return false;
                                const hasMatchingPublishStatus = workflow.templates.some(t =>
                                    (activeTab === 'published' ? t.isPublished : !t.isPublished)
                                );
                                if (!hasMatchingPublishStatus) return false;
                                const expectedTemplateType = getExpectedTemplateType();

                                if (expectedTemplateType) {
                                    const initialTemplate = workflow.templates[0];
                                
                                    return initialTemplate.templateType === expectedTemplateType ||
                                           initialTemplate.templateType === 'SITE_VISIT_TEMPLATE';
                                }
                                
                                return true;
                            })
                            .map((workflow) => {
                                const mainTemplate = workflow.templates[0];
                                const connectedTemplates = workflow.templates.slice(1);
                                const personaDetails = getPersonaDetails(mainTemplate);
                                const isExpanded = expandedWorkflows.has(workflow.id);

                                return (
                                    <div key={workflow.id} className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
                                        {/* Workflow Header - Always Visible */}
                                        <div className="p-4 bg-white border-b border-gray-200">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                                    <button
                                                        onClick={() => toggleWorkflowExpansion(workflow.id)}
                                                        className="flex-shrink-0 p-1 rounded-md hover:bg-gray-100 transition-colors"
                                                        title={isExpanded ? "Collapse workflow" : "Expand workflow"}
                                                    >
                                                        {isExpanded ? (
                                                            <ChevronDown size={16} className="text-gray-600" />
                                                        ) : (
                                                            <ChevronRight size={16} className="text-gray-600" />
                                                        )}
                                                    </button>
                                                    <Workflow size={18} className="text-flamingo-600 flex-shrink-0" />
                                                    <div className="flex flex-col min-w-0">
                                                        <h3 className="text-lg font-bold text-gray-900 truncate">
                                                            {mainTemplate.friendlyName || 'Unnamed Workflow'}
                                                        </h3>

                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    {activeTab === 'saved' ? (
                                                        <>
                                                            <button
                                                                onClick={() => handleViewWorkflow(workflow.id, true)}
                                                                disabled={isLoadingEdit}
                                                                className="px-3 py-1.5 text-sm bg-white border border-gray-200 text-flamingo-600 rounded-md hover:bg-gray-50 transition-colors font-medium"
                                                            >
                                                                {isLoadingEdit ? (
                                                                    <div className="flex items-center gap-1">
                                                                        <Loader2 size={14} className="animate-spin" />
                                                                        Loading...
                                                                    </div>
                                                                ) : (
                                                                    'View'
                                                                )}
                                                            </button>
                                                            <button
                                                                onClick={() => handlePublishClick(mainTemplate.customerWhatsappTemplateId)}
                                                                className="px-3 py-1.5 text-sm font-medium bg-flamingo-500 text-white rounded-md hover:bg-flamingo-400 transition-colors"
                                                            >
                                                                Publish
                                                            </button>
                                                            <button
                                                                onClick={() => toggleWorkflowExpansion(workflow.id)}
                                                                className="flex-shrink-0 p-2 rounded-md border border-gray-200 hover:bg-gray-100 transition-colors"
                                                                title={isExpanded ? "Collapse workflow" : "Expand workflow"}
                                                            >
                                                                {isExpanded ? (
                                                                    <ChevronUp size={16} className="text-gray-600" />
                                                                ) : (
                                                                    <ChevronDown size={16} className="text-gray-600" />
                                                                )}
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <div className="flex items-center gap-2 flex-shrink-0">
                                                            <button
                                                                onClick={() => handleViewWorkflow(workflow.id, false)}
                                                                className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors font-medium"
                                                            >
                                                                View Workflow
                                                            </button>
                                                            <button
                                                                onClick={() => toggleWorkflowExpansion(workflow.id)}
                                                                className="flex-shrink-0 p-2 border border-gray-200 rounded-md hover:bg-gray-100 transition-colors"
                                                                title={isExpanded ? "Collapse workflow" : "Expand workflow"}
                                                            >
                                                                {isExpanded ? (
                                                                    <ChevronUp size={16} className="text-gray-600" />
                                                                ) : (
                                                                    <ChevronDown size={16} className="text-gray-600" />
                                                                )}
                                                            </button></div>

                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Collapsible Content */}
                                        {isExpanded && (
                                            <div className="p-4">
                                                {/* Workflow Info */}
                                                <div className="flex items-center gap-4 mb-4 text-sm text-gray-600">
                                                    <div className="flex items-center gap-1">
                                                        <MessageSquare size={14} />
                                                        <span>{workflow.templates.length} template(s)</span>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <Users size={14} />
                                                        <span>{personaDetails.count} persona(s)</span>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <Calendar size={14} />
                                                        <span>{formatDate(mainTemplate.sendDatetime)}</span>
                                                    </div>
                                                </div>

                                                {/* Workflow Flow Visualization */}
                                                <div className="bg-white rounded-lg p-4 border border-gray-200">
                                                    <div className="flex items-start gap-4 overflow-x-auto pb-2">
                                                        {/* Main Template */}
                                                        <div className="flex-shrink-0">
                                                            {renderTemplateCard(mainTemplate, true)}
                                                        </div>

                                                        {/* Arrow and Connected Templates */}
                                                        {connectedTemplates.length > 0 && (
                                                            <>
                                                                <div className="flex items-center flex-shrink-0 pt-16">
                                                                    <ArrowRight size={20} className="text-gray-400" />
                                                                </div>

                                                                <div className="flex gap-4 flex-shrink-0">
                                                                    {connectedTemplates.map((template, index) => (
                                                                        <div key={template.customerWhatsappTemplateId || index}>
                                                                            {renderTemplateCard(template, false)}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>

                                                    {/* Connection Info */}
                                                    {workflow.connections && workflow.connections.length > 0 && (
                                                        <div className="mt-4 pt-3 border-t border-gray-100">
                                                            <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                                                                Reply Connections
                                                            </h5>
                                                            <div className="space-y-1">
                                                                {workflow.connections.map((connection, index) => (
                                                                    <div key={index} className="flex items-center gap-2 text-xs text-gray-600">
                                                                        <span className="w-2 h-2 bg-flamingo-400 rounded-full"></span>
                                                                        <span className="font-medium">"{connection.replyMessage}"</span>
                                                                        <ArrowRight size={12} className="text-gray-400" />
                                                                        <span>Next template</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                    </div>
                </>
            )}

            <ConfirmationModal
                isOpen={publishConfirmation.show}
                onConfirm={handlePublishConfirm}
                onCancel={handlePublishCancel}
                message="Are you sure you want to publish this workflow? Once published, it cannot be edited."
                isLoading={isPublishing}
                loadingText="Publishing..."
            />
        </div>
    );
};

export default PreviousTemplates;